/**
 * حساب‌های بانکیِ دیگرِ یک پرسنل (v2.160.0).
 *
 * کاربر (۱۴۰۵/۰۸/۰۷): «برای کلِ پرسنل یه حسابِ دیگه هم اضافه کنیم؛ اصلی/غیراصلی، تیکش رو بزنیم؛ اصلی در پرداختی‌ها بیاد».
 * حسابِ اصلی همان فیلدهای بانکیِ بالای همین فرم است (پرداخت‌ها و فیش از آن می‌خوانند)؛ اینجا حساب‌های دیگر اضافه می‌شوند و
 * «اصلی شود» جای آن‌ها را با اصلی عوض می‌کند — فیلدهای فرم هم همان لحظه به‌روز می‌شوند تا ذخیرهٔ فرم چیزی را برنگرداند.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Input, Popconfirm, Row, Space, Tag, Tooltip, Typography, message } from "antd";
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, PlusOutlined, StarOutlined } from "@ant-design/icons";
import {
  addBankAccount, deleteBankAccount, getBankAccounts, makePrimaryBankAccount, updateBankAccount,
} from "../api/client";
import { apiError } from "../utils/apiError";

const { Text } = Typography;
const mono = { fontFamily: "monospace", letterSpacing: 1 };

export default function BankAccountsPanel({ employeeId, onPrimaryChanged }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(null);       // {id?, bank_name, account_number, iban, card_number, note} — id ⇒ ویرایش
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!employeeId) return;
    getBankAccounts(employeeId).then((r) => setRows(r.data || [])).catch(() => {});
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  if (!employeeId) {
    return <Text type="secondary" style={{ fontSize: 12 }}>حسابِ دوم را بعد از ذخیرهٔ پرونده می‌شود اضافه کرد.</Text>;
  }

  const save = async () => {
    setBusy(true);
    try {
      // v2.166.0 (کاربر): «حساب‌های ثانویه‌ای که قبلاً زدیم قابلِ ویرایش نیست» — حالا همان فرم برای ویرایش
      const { id, ...body } = draft;
      const r = id ? await updateBankAccount(employeeId, id, body) : await addBankAccount(employeeId, body);
      setRows(r.data || []); setDraft(null); message.success(id ? "حساب ویرایش شد" : "حساب اضافه شد");
    } catch (e) { message.error(apiError(e, "ثبت نشد")); } finally { setBusy(false); }
  };
  const makePrimary = async (a) => {
    try {
      const r = await makePrimaryBankAccount(employeeId, a.id);
      const list = r.data || [];
      setRows(list);
      const p = list.find((x) => x.is_primary);
      if (p && onPrimaryChanged) onPrimaryChanged(p);
      message.success("حسابِ اصلی عوض شد — پرداخت‌ها از این به بعد به این حساب");
    } catch (e) { message.error(apiError(e, "انجام نشد")); }
  };
  const remove = async (a) => {
    try { const r = await deleteBankAccount(employeeId, a.id); setRows(r.data || []); }
    catch (e) { message.error(apiError(e, "حذف نشد")); }
  };

  return (
    <Card size="small" title="حساب‌های بانکی" style={{ marginTop: 8 }}
      extra={!draft && <Button size="small" icon={<PlusOutlined />} onClick={() => setDraft({})}>حسابِ دیگر</Button>}>
      {rows.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>هنوز حسابی ثبت نشده.</Text>}
      {rows.map((a) => (
        <div key={a.id} style={{ padding: "6px 0", borderBottom: "1px solid var(--border, #eee)" }}>
          <Space wrap size={6}>
            {a.is_primary
              ? <Tag color="green" icon={<CheckCircleOutlined />}>اصلی — پرداخت‌ها</Tag>
              : <Tag>{a.note || "غیراصلی"}</Tag>}
            <b>{a.iban_bank || a.bank_name || "بانک؟"}</b>
            {a.account_number && <Text style={mono}>حساب {a.account_number}</Text>}
            {a.iban && <Text style={mono}>{a.iban}</Text>}
            {a.iban && a.iban_valid === false && <Tag color="red">شبا نامعتبر</Tag>}
            {a.iban_message && a.iban_valid && <Tooltip title={a.iban_message}><Tag color="orange">بانک جور نیست</Tag></Tooltip>}
            {!a.is_primary && (
              <>
                <Button size="small" type="text" icon={<EditOutlined />} title="ویرایش"
                  onClick={() => setDraft({ id: a.id, bank_name: a.bank_name || "", account_number: a.account_number || "",
                    iban: a.iban || "", card_number: a.card_number || "", note: a.note || "" })} />
                <Popconfirm title="این حساب اصلی شود؟ حقوق و پرداخت‌ها از این به بعد به این حساب می‌رود؛ اصلیِ فعلی غیراصلی می‌ماند."
                  okText="بله" cancelText="خیر" onConfirm={() => makePrimary(a)}>
                  <Button size="small" icon={<StarOutlined />}>اصلی شود</Button>
                </Popconfirm>
                <Popconfirm title="این حساب حذف شود؟" okText="حذف" cancelText="خیر" onConfirm={() => remove(a)}>
                  <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                </Popconfirm>
              </>
            )}
          </Space>
        </div>
      ))}
      {draft && (
        <div style={{ marginTop: 8 }}>
          <Row gutter={8}>
            <Col xs={24} sm={8}><Input placeholder="نام بانک (از شبا خودکار)" value={draft.bank_name}
              onChange={(e) => setDraft({ ...draft, bank_name: e.target.value })} /></Col>
            <Col xs={24} sm={8}><Input placeholder="شماره حساب" style={mono} value={draft.account_number}
              onChange={(e) => setDraft({ ...draft, account_number: e.target.value })} /></Col>
            <Col xs={24} sm={8}><Input placeholder="یادداشت — مثلاً «حسابِ بیمه»" value={draft.note}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })} /></Col>
          </Row>
          <Row gutter={8} style={{ marginTop: 6 }}>
            <Col xs={24} sm={14}><Input placeholder="شبا IR…" maxLength={32} style={mono} value={draft.iban}
              onChange={(e) => setDraft({ ...draft, iban: e.target.value })} /></Col>
            <Col xs={24} sm={10}><Input placeholder="شماره کارت" maxLength={19} style={mono} value={draft.card_number}
              onChange={(e) => setDraft({ ...draft, card_number: e.target.value })} /></Col>
          </Row>
          <Space style={{ marginTop: 6 }}>
            <Button type="primary" size="small" loading={busy} onClick={save}>{draft.id ? "ذخیرهٔ ویرایش" : "ثبتِ حساب"}</Button>
            <Button size="small" onClick={() => setDraft(null)}>انصراف</Button>
            <Text type="secondary" style={{ fontSize: 11 }}>شبا با رقمِ کنترل بررسی می‌شود؛ شبای غلط ثبت نمی‌شود.</Text>
          </Space>
        </div>
      )}
    </Card>
  );
}
